import { z } from 'zod';
import {
  ACADEMIC_PERIOD_STATUS_VALUES,
  CALENDAR_EVENT_TYPE_VALUES,
  DAILY_SEGMENT_VALUES,
  EMPLOYMENT_STATUS_VALUES,
  INTEGRATION_EVENT_TYPES,
  INTEGRATION_SYNC_KIND_VALUES,
  INTEGRATION_SYNC_STATUS_VALUES,
  LEAVE_TYPE_VALUES,
  LOAD_TYPE_VALUES,
  ONLINE_PLATFORM_VALUES,
  SCHEDULED_MODALITY_VALUES,
  SHIFT_MODEL_VALUES,
  SYNC_MAX_ITEMS,
  TERM_TYPE_VALUES,
  WEBHOOK_DELIVERY_STATUS_VALUES,
  type IntegrationEventType,
} from '@fas/types';
import { roleAssignmentSchema } from './auth';
import { booleanQuerySchema, codeSchema, emailSchema, isoDateSchema, nonEmptyTrimmed, optionalText, paginationQuerySchema, timeOfDaySchema, uuidSchema } from './common';
import { employeeNumberSchema, mobileNumberSchema, workConfigIssues } from './faculty';

// ── Integration API (SPEC §68, Phase 3A, ADR-054 – ADR-056) ────────────────────
/** `ic_` and 20 lower-case base32 characters. */
export const CLIENT_ID_PATTERN = /^ic_[a-z2-7]{20}$/;
/** `fas_cs_` and 43 base64url characters (256 random bits). */
export const CLIENT_SECRET_PATTERN = /^fas_cs_[A-Za-z0-9_-]{43}$/;
/** `whsec_` and 43 base64url characters (256 random bits). */
export const WEBHOOK_SECRET_PATTERN = /^whsec_[A-Za-z0-9_-]{43}$/;

function ipv4Octets(s: string): number[] | null {
  const parts = s.split('.');
  if (parts.length !== 4) return null;
  const out: number[] = [];
  for (const p of parts) {
    // No leading zeros: "010" means 8 to some parsers and 10 to others.
    if (!/^(0|[1-9]\d{0,2})$/.test(p)) return null;
    const n = Number(p);
    if (n > 255) return null;
    out.push(n);
  }
  return out;
}

function ipv6Words(input: string): number[] | null {
  let s = input.toLowerCase();
  if (!/^[0-9a-f:.]+$/.test(s)) return null;
  let v4: number[] = [];
  if (s.includes('.')) {
    const i = s.lastIndexOf(':');
    const octets = i < 0 ? null : ipv4Octets(s.slice(i + 1));
    if (!octets) return null;
    v4 = [(octets[0]! << 8) | octets[1]!, (octets[2]! << 8) | octets[3]!];
    s = s.slice(0, i + 1);
    if (!s.endsWith('::')) s = s.slice(0, -1);
  }
  const halves = s.split('::');
  if (halves.length > 2) return null;
  const groups = (part: string) => (part === '' ? [] : part.split(':'));
  const head = groups(halves[0]!);
  const tail = halves.length === 2 ? groups(halves[1]!) : [];
  if ([...head, ...tail].some((g) => !/^[0-9a-f]{1,4}$/.test(g))) return null;
  const hex = (g: string) => parseInt(g, 16);
  const count = head.length + tail.length + v4.length;
  if (halves.length === 2) {
    // "::" stands for at least one group of zeros.
    if (count > 7) return null;
    return [...head.map(hex), ...new Array<number>(8 - count).fill(0), ...tail.map(hex), ...v4];
  }
  return count === 8 ? [...head.map(hex), ...v4] : null;
}

/** IP version of an address, or null when it is not one (no zone ids, no host names). */
export function ipFamily(address: string): 4 | 6 | null {
  if (ipv4Octets(address)) return 4;
  if (ipv6Words(address)) return 6;
  return null;
}

/**
 * An address or a CIDR range ("203.0.113.7", "10.20.0.0/16", "2001:db8::/32"), normalized to
 * lower case; null when it is neither.
 */
export function normalizeIpRange(input: string): string | null {
  const value = input.trim().toLowerCase();
  const [address, prefix, extra] = value.split('/');
  if (extra !== undefined || !address) return null;
  const family = ipFamily(address);
  if (!family) return null;
  if (prefix === undefined) return address;
  if (!/^\d{1,3}$/.test(prefix)) return null;
  const bits = Number(prefix);
  if (bits > (family === 4 ? 32 : 128)) return null;
  return `${address}/${bits}`;
}

export const ipRangeSchema = z
  .string()
  .max(50, 'At most 50 characters')
  .transform((v, ctx) => {
    const normalized = normalizeIpRange(v);
    if (!normalized) {
      ctx.addIssue({ code: 'custom', message: `"${v.trim()}" is not an IP address or a CIDR range such as 10.20.0.0/16` });
      return z.NEVER;
    }
    return normalized;
  });

/** Addresses the client may call from; an empty list allows any address. */
export const allowedIpsSchema = z
  .array(ipRangeSchema)
  .max(20, 'At most 20 addresses or ranges')
  .transform((list) => [...new Set(list)]);

/** Secret lifetime in days; null for a secret that does not expire. */
export const secretLifetimeSchema = z.number().int('Whole days').min(1, 'At least 1 day').max(730, 'At most 730 days (two years)').nullable();

export const createIntegrationClientSchema = z
  .object({
    name: nonEmptyTrimmed(100, 'Name'),
    description: optionalText(500),
    allowedIps: allowedIpsSchema.default([]),
    secretExpiresInDays: secretLifetimeSchema.default(365),
    roles: z.array(roleAssignmentSchema).max(10).default([]),
  })
  .strict();
export type CreateIntegrationClientInput = z.infer<typeof createIntegrationClientSchema>;

export const updateIntegrationClientSchema = z
  .object({
    name: nonEmptyTrimmed(100, 'Name').optional(),
    description: optionalText(500),
    allowedIps: allowedIpsSchema.optional(),
    version: z.number().int().min(1),
  })
  .strict();
export type UpdateIntegrationClientInput = z.infer<typeof updateIntegrationClientSchema>;

export const disableIntegrationClientSchema = z.object({ reason: nonEmptyTrimmed(500, 'Reason') }).strict();
export type DisableIntegrationClientInput = z.infer<typeof disableIntegrationClientSchema>;

export const createClientSecretSchema = z.object({ expiresInDays: secretLifetimeSchema.default(365) }).strict();
export type CreateClientSecretInput = z.infer<typeof createClientSecretSchema>;

export const listIntegrationClientsQuerySchema = paginationQuerySchema.extend({ active: booleanQuerySchema.optional() });
export type ListIntegrationClientsQuery = z.infer<typeof listIntegrationClientsQuerySchema>;

/**
 * An http(s) URL without credentials or fragment. Whether plain HTTP or a given address is
 * allowed depends on the server's configuration and is checked there (ADR-056).
 */
export const webhookUrlSchema = z
  .string('Enter the URL')
  .trim()
  .min(1, 'Enter the URL')
  .max(2000, 'At most 2000 characters')
  .pipe(z.url('Enter a full URL, e.g. https://hr.example.edu/fas/events'))
  .superRefine((v, ctx) => {
    let url: URL;
    try {
      url = new URL(v);
    } catch {
      return; // already reported by z.url()
    }
    if (url.protocol !== 'https:' && url.protocol !== 'http:') ctx.addIssue({ code: 'custom', message: 'Use an https:// URL' });
    if (url.username || url.password) ctx.addIssue({ code: 'custom', message: 'Do not put credentials in the URL; requests are signed instead' });
    if (url.hash) ctx.addIssue({ code: 'custom', message: 'Remove the #fragment' });
  });

export const webhookEventTypesSchema = z
  .array(z.enum(INTEGRATION_EVENT_TYPES))
  .min(1, 'Choose at least one event')
  .max(50)
  .transform((list) => [...new Set(list)] as IntegrationEventType[]);

export const createWebhookSchema = z
  .object({
    clientId: uuidSchema,
    name: nonEmptyTrimmed(100, 'Name'),
    url: webhookUrlSchema,
    eventTypes: webhookEventTypesSchema,
  })
  .strict();
export type CreateWebhookInput = z.infer<typeof createWebhookSchema>;

export const updateWebhookSchema = z
  .object({
    name: nonEmptyTrimmed(100, 'Name').optional(),
    url: webhookUrlSchema.optional(),
    eventTypes: webhookEventTypesSchema.optional(),
    active: z.boolean().optional(),
    version: z.number().int().min(1),
  })
  .strict();
export type UpdateWebhookInput = z.infer<typeof updateWebhookSchema>;

export const listWebhooksQuerySchema = paginationQuerySchema.extend({
  clientId: uuidSchema.optional(),
  active: booleanQuerySchema.optional(),
});
export type ListWebhooksQuery = z.infer<typeof listWebhooksQuerySchema>;

export const listWebhookDeliveriesQuerySchema = paginationQuerySchema.extend({ status: z.enum(WEBHOOK_DELIVERY_STATUS_VALUES).optional() });
export type ListWebhookDeliveriesQuery = z.infer<typeof listWebhookDeliveriesQuerySchema>;

// ── SIS sync (Phase 3C, ADR-058) ───────────────────────────────────────────────
/** An identifier in the SIS: any printable text; references between items use it. */
export const externalIdSchema = z
  .string('Required')
  .trim()
  .min(1, 'Required')
  .max(100, 'At most 100 characters')
  .regex(/^[^\u0000-\u001f\u007f]+$/, 'No control characters');

const noteText = (max: number) => z.string().trim().max(max).nullable().optional();
const units = z.number().min(0).max(99).nullable().optional();
const hours = z.number().min(0).max(168).nullable().optional();
/** A department code, or COLLEGE/DEPARTMENT where the code exists in several colleges. */
const departmentRef = codeSchema(41);
/** A program code (of the section's department), or DEPARTMENT/PROGRAM. */
const programRef = codeSchema(51);

const sisAcademicYearSchema = z
  .object({
    externalId: externalIdSchema,
    code: codeSchema(20),
    name: nonEmptyTrimmed(100, 'Name'),
    startDate: isoDateSchema,
    endDate: isoDateSchema,
    status: z.enum(ACADEMIC_PERIOD_STATUS_VALUES).optional(),
  })
  .strict()
  .refine((v) => v.endDate > v.startDate, { path: ['endDate'], message: 'Must be after the start date' });

const sisTermSchema = z
  .object({
    externalId: externalIdSchema,
    /** externalId of the academic year. */
    academicYear: externalIdSchema,
    code: codeSchema(20),
    name: nonEmptyTrimmed(100, 'Name'),
    termType: z.enum(TERM_TYPE_VALUES),
    sequence: z.number().int().min(1).max(12),
    startDate: isoDateSchema,
    endDate: isoDateSchema,
    status: z.enum(ACADEMIC_PERIOD_STATUS_VALUES).optional(),
  })
  .strict()
  .refine((v) => v.endDate > v.startDate, { path: ['endDate'], message: 'Must be after the start date' });

const sisCourseSchema = z
  .object({
    externalId: externalIdSchema,
    code: codeSchema(30),
    title: nonEmptyTrimmed(200, 'Title'),
    description: noteText(2000),
    /** Department in this system; departments are maintained here. */
    department: departmentRef.nullable().optional(),
    lectureUnits: units,
    labUnits: units,
    lectureHoursPerWeek: hours,
    labHoursPerWeek: hours,
    active: z.boolean().optional(),
  })
  .strict();

const sisSectionSchema = z
  .object({
    externalId: externalIdSchema,
    course: externalIdSchema,
    term: externalIdSchema,
    sectionCode: codeSchema(30),
    /** Offering department (default: the course's). */
    department: departmentRef.nullable().optional(),
    program: programRef.nullable().optional(),
    capacity: z.number().int().min(0).max(10000).nullable().optional(),
    active: z.boolean().optional(),
  })
  .strict();

const sisLoadSchema = z
  .object({
    externalId: externalIdSchema,
    employeeNumber: z.string().trim().min(1).max(30),
    /** The term (externalId); required when the load is not for a section. */
    term: externalIdSchema.optional(),
    section: externalIdSchema.optional(),
    loadType: z.enum(LOAD_TYPE_VALUES),
    units,
    contactHoursPerWeek: hours,
    description: noteText(500),
    active: z.boolean().optional(),
  })
  .strict()
  .refine((v) => v.section || v.term, { path: ['term'], message: 'Give the section or, for a load without a section, the term' })
  .refine((v) => v.section || v.description, { path: ['description'], message: 'Describe a load that is not for a section' });

const sisScheduleSchema = z
  .object({
    externalId: externalIdSchema,
    section: externalIdSchema,
    employeeNumber: z.string().trim().min(1).max(30),
    /** The faculty load (externalId) the meeting belongs to, if the SIS sends loads. */
    load: externalIdSchema.optional(),
    dayOfWeek: z.number().int().min(1, 'Day must be 1 (Monday) to 7 (Sunday)').max(7, 'Day must be 1 (Monday) to 7 (Sunday)'),
    startTime: timeOfDaySchema,
    endTime: timeOfDaySchema,
    /** Location code (room, laboratory, online or affiliate site). */
    room: z.string().trim().min(1).max(40).nullable().optional(),
    modality: z.enum(SCHEDULED_MODALITY_VALUES),
    loadType: z.enum(LOAD_TYPE_VALUES).optional(),
    onlinePlatform: z.enum(ONLINE_PLATFORM_VALUES).nullable().optional(),
    meetingUrl: z.string().trim().max(500).pipe(z.url({ protocol: /^https$/, error: 'Meeting link must be an https:// URL' })).nullable().optional(),
    effectiveFrom: isoDateSchema.optional(),
    effectiveTo: isoDateSchema.optional(),
    active: z.boolean().optional(),
  })
  .strict()
  .refine((v) => v.startTime !== v.endTime, { path: ['endTime'], message: 'End time must differ from start time' })
  .refine((v) => !v.effectiveFrom || !v.effectiveTo || v.effectiveTo >= v.effectiveFrom, { path: ['effectiveTo'], message: 'Must be on or after effectiveFrom' });

const unique =
  <T extends { externalId: string }>(label: string) =>
  (list: T[], ctx: z.RefinementCtx) => {
    const seen = new Set<string>();
    list.forEach((item, i) => {
      if (seen.has(item.externalId)) ctx.addIssue({ code: 'custom', path: [i, 'externalId'], message: `${label} ${item.externalId} appears twice` });
      seen.add(item.externalId);
    });
  };

/**
 * `POST /integrations/sis/sync`: records keyed by SIS identifiers, processed together as one
 * all-or-nothing change (ADR-058). `complete` declares that the batch holds every load and
 * schedule of a term (optionally of some departments): those synced earlier but missing now are
 * deactivated.
 */
export const sisSyncSchema = z
  .object({
    dryRun: z.boolean().default(false),
    academicYears: z.array(sisAcademicYearSchema).max(20).default([]).superRefine(unique('Academic year')),
    terms: z.array(sisTermSchema).max(50).default([]).superRefine(unique('Term')),
    courses: z.array(sisCourseSchema).max(2000).default([]).superRefine(unique('Course')),
    sections: z.array(sisSectionSchema).max(2000).default([]).superRefine(unique('Section')),
    loads: z.array(sisLoadSchema).max(2000).default([]).superRefine(unique('Load')),
    schedules: z.array(sisScheduleSchema).max(2000).default([]).superRefine(unique('Schedule')),
    complete: z
      .array(z.object({ term: externalIdSchema, departments: z.array(departmentRef).min(1).max(50).optional() }).strict())
      .max(20)
      .default([]),
  })
  .strict()
  .superRefine((v, ctx) => {
    const total = v.academicYears.length + v.terms.length + v.courses.length + v.sections.length + v.loads.length + v.schedules.length;
    if (total === 0 && v.complete.length === 0) ctx.addIssue({ code: 'custom', path: [], message: 'Send at least one record' });
    if (total > SYNC_MAX_ITEMS) ctx.addIssue({ code: 'custom', path: [], message: `At most ${SYNC_MAX_ITEMS} records per sync; split the batch` });
  });
export type SisSyncInput = z.infer<typeof sisSyncSchema>;
export type SisAcademicYearInput = SisSyncInput['academicYears'][number];
export type SisTermInput = SisSyncInput['terms'][number];
export type SisCourseInput = SisSyncInput['courses'][number];
export type SisSectionInput = SisSyncInput['sections'][number];
export type SisLoadInput = SisSyncInput['loads'][number];
export type SisScheduleInput = SisSyncInput['schedules'][number];

// ── HRIS sync (Phase 3D, ADR-059) ──────────────────────────────────────────────
const dayCount = (from: string, to: string) => (Date.parse(to) - Date.parse(from)) / 86_400_000;

/** A full-time faculty member's work arrangement (§21); required when one is hired. */
const hrisWorkSchema = z
  .object({
    shiftModel: z.enum(SHIFT_MODEL_VALUES).nullable().optional(),
    regularWorkStart: timeOfDaySchema.nullable().optional(),
    regularWorkEnd: timeOfDaySchema.nullable().optional(),
    breakStart: timeOfDaySchema.nullable().optional(),
    breakEnd: timeOfDaySchema.nullable().optional(),
    workDays: z
      .array(z.number().int().min(1).max(7))
      .max(7)
      .refine((d) => new Set(d).size === d.length, 'Work days must be unique')
      .optional(),
    dailyRequiredMinutes: z.number().int().min(0).max(1440).nullable().optional(),
    weeklyRequiredMinutes: z.number().int().min(0).max(10080).nullable().optional(),
  })
  .strict()
  .superRefine((v, ctx) => {
    for (const issue of workConfigIssues(v)) ctx.addIssue({ code: 'custom', path: [issue.path], message: issue.message });
  });

/** A faculty member, keyed by employee number; the HRIS owns these fields. */
const hrisFacultySchema = z
  .object({
    employeeNumber: employeeNumberSchema,
    firstName: nonEmptyTrimmed(100, 'First name'),
    middleName: optionalText(100),
    lastName: nonEmptyTrimmed(100, 'Last name'),
    suffix: optionalText(20),
    institutionalEmail: emailSchema,
    mobileNumber: mobileNumberSchema,
    /** Employment type code configured here (e.g. PART_TIME, FULL_TIME). */
    employmentType: z.string().trim().toUpperCase().min(1, 'Employment type is required').max(30),
    /** SEPARATED and INACTIVE end the linked account's access. */
    employmentStatus: z.enum(EMPLOYMENT_STATUS_VALUES),
    department: departmentRef.nullable().optional(),
    /** College code, for someone placed in a college but no department. */
    college: codeSchema(20).nullable().optional(),
    positionTitle: optionalText(100),
    academicRank: optionalText(100),
    hireDate: isoDateSchema.nullable().optional(),
    work: hrisWorkSchema.optional(),
    /** A newly hired faculty member also gets a sign-in account and a password-setup e-mail. */
    createAccount: z.boolean().default(true),
  })
  .strict();

const hrisCalendarEventSchema = z
  .object({
    externalId: externalIdSchema,
    eventType: z.enum(CALENDAR_EVENT_TYPE_VALUES),
    title: nonEmptyTrimmed(200, 'Title'),
    description: optionalText(1000),
    startDate: isoDateSchema,
    endDate: isoDateSchema,
    startTime: timeOfDaySchema.nullable().optional(),
    endTime: timeOfDaySchema.nullable().optional(),
    /** At most one of campus, college or department (none: everyone). */
    campus: codeSchema(20).nullable().optional(),
    college: codeSchema(20).nullable().optional(),
    department: departmentRef.nullable().optional(),
    /** Default by event type: holidays and suspensions stop classes. */
    suspendsClasses: z.boolean().optional(),
    suspendsWork: z.boolean().optional(),
    active: z.boolean().optional(),
  })
  .strict()
  .superRefine((v, ctx) => {
    if (v.endDate < v.startDate) ctx.addIssue({ code: 'custom', path: ['endDate'], message: 'Must be on or after the start date' });
    else if (dayCount(v.startDate, v.endDate) > 366) ctx.addIssue({ code: 'custom', path: ['endDate'], message: 'The range cannot exceed 366 days' });
    if ((v.startTime || v.endTime) && v.startDate !== v.endDate) ctx.addIssue({ code: 'custom', path: ['startTime'], message: 'Times can only be set for a single-day event' });
    if (v.startTime && v.endTime && v.endTime <= v.startTime) ctx.addIssue({ code: 'custom', path: ['endTime'], message: 'End time must be after the start time' });
    if ([v.campus, v.college, v.department].filter(Boolean).length > 1) {
      ctx.addIssue({ code: 'custom', path: ['department'], message: 'Limit the event to one campus, college or department (or none for everyone)' });
    }
  });

/** Leave approved in the HRIS: recorded here as approved, without a second approval. */
const hrisLeaveSchema = z
  .object({
    externalId: externalIdSchema,
    employeeNumber: employeeNumberSchema,
    leaveType: z.enum(LEAVE_TYPE_VALUES),
    startDate: isoDateSchema,
    endDate: isoDateSchema,
    portion: z.enum(DAILY_SEGMENT_VALUES).default('FULL_DAY'),
    hours: z.number().min(0.5).max(2000).nullable().optional(),
    /** Visible to the faculty member and leave approvers, never on events. */
    reason: z.string().trim().max(1000).optional(),
    /** false: the leave was cancelled in the HRIS. */
    active: z.boolean().optional(),
  })
  .strict()
  .superRefine((v, ctx) => {
    if (v.endDate < v.startDate) ctx.addIssue({ code: 'custom', path: ['endDate'], message: 'Must be on or after the start date' });
    else if (dayCount(v.startDate, v.endDate) > 366) ctx.addIssue({ code: 'custom', path: ['endDate'], message: 'The range cannot exceed 366 days' });
    if (v.portion !== 'FULL_DAY' && v.startDate !== v.endDate) ctx.addIssue({ code: 'custom', path: ['portion'], message: 'A half-day leave covers a single date' });
    // Balances are kept per calendar year (ADR-049).
    if (v.startDate.slice(0, 4) !== v.endDate.slice(0, 4)) {
      ctx.addIssue({ code: 'custom', path: ['endDate'], message: 'Leave counts against one calendar year; send the days of each year as separate records' });
    }
  });

const uniqueBy =
  <T>(label: string, key: (item: T) => string) =>
  (list: T[], ctx: z.RefinementCtx) => {
    const seen = new Set<string>();
    list.forEach((item, i) => {
      const k = key(item);
      if (seen.has(k)) ctx.addIssue({ code: 'custom', path: [i], message: `${label} ${k} appears twice` });
      seen.add(k);
    });
  };

/**
 * `POST /integrations/hris/sync`: faculty keyed by employee number, calendar events and leave
 * keyed by the HRIS's identifiers, processed together as one all-or-nothing change (ADR-059).
 */
export const hrisSyncSchema = z
  .object({
    dryRun: z.boolean().default(false),
    faculty: z.array(hrisFacultySchema).max(2000).default([]).superRefine(uniqueBy('Employee number', (f) => f.employeeNumber)),
    calendarEvents: z.array(hrisCalendarEventSchema).max(500).default([]).superRefine(uniqueBy('Calendar event', (e) => e.externalId)),
    leave: z.array(hrisLeaveSchema).max(2000).default([]).superRefine(uniqueBy('Leave', (l) => l.externalId)),
  })
  .strict()
  .superRefine((v, ctx) => {
    const total = v.faculty.length + v.calendarEvents.length + v.leave.length;
    if (total === 0) ctx.addIssue({ code: 'custom', path: [], message: 'Send at least one record' });
    if (total > SYNC_MAX_ITEMS) ctx.addIssue({ code: 'custom', path: [], message: `At most ${SYNC_MAX_ITEMS} records per sync; split the batch` });
  });
export type HrisSyncInput = z.infer<typeof hrisSyncSchema>;
export type HrisFacultyInput = HrisSyncInput['faculty'][number];
export type HrisCalendarEventInput = HrisSyncInput['calendarEvents'][number];
export type HrisLeaveInput = HrisSyncInput['leave'][number];

export const listSyncRunsQuerySchema = paginationQuerySchema.extend({
  clientId: uuidSchema.optional(),
  kind: z.enum(INTEGRATION_SYNC_KIND_VALUES).optional(),
  status: z.enum(INTEGRATION_SYNC_STATUS_VALUES).optional(),
});
export type ListSyncRunsQuery = z.infer<typeof listSyncRunsQuerySchema>;

/**
 * `GET /integrations/events?after=<sequence>&limit=<n>&type=a,b` — the event feed, oldest first.
 * `order=desc&before=<sequence>` reads it newest first (the administrators' event log).
 */
export const integrationFeedQuerySchema = z
  .object({
    after: z.coerce.number().int().min(0).max(Number.MAX_SAFE_INTEGER).default(0),
    before: z.coerce.number().int().min(1).max(Number.MAX_SAFE_INTEGER).optional(),
    order: z.enum(['asc', 'desc']).default('asc'),
    limit: z.coerce.number().int().min(1).max(500).default(100),
    type: z
      .string()
      .trim()
      .max(2000)
      .optional()
      .transform((v, ctx) => {
        if (!v) return undefined;
        const types = [...new Set(v.split(',').map((t) => t.trim()).filter(Boolean))];
        const known = new Set<string>(INTEGRATION_EVENT_TYPES);
        const unknown = types.filter((t) => !known.has(t));
        if (unknown.length) {
          ctx.addIssue({ code: 'custom', message: `Unknown event type: ${unknown.join(', ')}` });
          return z.NEVER;
        }
        return types as IntegrationEventType[];
      }),
  })
  .strict();
export type IntegrationFeedQuery = z.infer<typeof integrationFeedQuerySchema>;
